import { useEffect, useRef } from 'react';

/**
 * MOBILE-FIT M2 (v3.34) — the ONE horizontal-slide container for wide data.
 *
 * Owner's rule: whatever does not fit must slide; whatever does not slide must
 * be fully visible. Wide tables (DCMA, activity tables, the Oversight users
 * table, the HowToRead population table) live inside this container: same
 * columns, same data, same order as desktop — nothing hidden, nothing dropped.
 *
 * `data-scroll="x"` is the responsive gate's whitelist marker (the only
 * elements allowed `scrollWidth > clientWidth`). The right-edge fade appears
 * only while the container ACTUALLY overflows and is not scrolled to the end —
 * measured via ResizeObserver + scroll events, never assumed.
 */
export function ScrollX({ className = '', children, ...rest }) {
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const measure = () => {
      const overflowing = el.scrollWidth > el.clientWidth + 1;
      el.dataset.overflowing = overflowing ? 'true' : 'false';
      const atEnd = el.scrollLeft + el.clientWidth >= el.scrollWidth - 1;
      el.dataset.atEnd = atEnd ? 'true' : 'false';
    };
    measure();
    el.addEventListener('scroll', measure, { passive: true });
    let ro = null;
    if (typeof ResizeObserver === 'function') {
      ro = new ResizeObserver(measure);
      ro.observe(el);
      if (el.firstElementChild) ro.observe(el.firstElementChild);
    }
    return () => {
      el.removeEventListener('scroll', measure);
      if (ro) ro.disconnect();
    };
  }, []);

  return (
    <div ref={ref} data-scroll="x" className={`scroll-x ${className}`.trim()} {...rest}>
      {children}
    </div>
  );
}
